import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../api";
import { Modal, Spinner, useToast } from "../../components/ui";

/*
 * Daily import value-reconciliation gate.
 *
 * When an admin opens Inventory, any record that is assigned to a PHS user but NOT yet worked here, and
 * which the latest sheet re-imported at a DIFFERENT stage/value (e.g. it was "Auth Needed" in a user's
 * queue and an outsider submitted it so it came back as "Request Pending"), is surfaced here as a
 * conflict. The admin must clear every row — keep existing value, keep new value, or set a specific
 * value — before they can allocate anything else. The dialog cannot be dismissed while rows remain.
 *
 * This is SEPARATE from the "reworked" flow (which handles records already worked in PHS); that is
 * untouched. Only the ADMIN role is gated here.
 *
 * Layout notes:
 *   - Every row is a SINGLE line and the whole dialog fits with NO horizontal scroll.
 *   - The UID is the composite key MRN + Order + OrderDate + RTCdate glued together. MRN and Order
 *     already have their own columns, so re-printing the whole ~70-char string is what used to force a
 *     sideways scroll. We therefore show only the part of the UID that ISN'T already on the row — the
 *     two dates — and keep the COMPLETE UID available on hover (title). Nothing is lost.
 *   - The list is PAGINATED (PAGE_SIZE rows per page), so the dialog stays the same fixed, compact
 *     height whether there is 1 record or 29 — it does not grow tall and it does not scroll vertically.
 *   - A running number column lets the admin clear rows in order; cleared rows drop out and the
 *     remaining ones re-flow up.
 */

const VALUE_OPTIONS = ["Auth Needed", "Request Pending", "Modification"];

// Rows shown per page — single-line rows, so this fixes the dialog height.
const PAGE_SIZE = 8;

// The UID looks like "810143Physical Therapy10/07/202610/14/2026" (MRN + Order + OrderDate + RTCdate).
// MRN and Order are already their own columns, so show only the trailing dates (the unique part) and
// leave the full UID on hover. Robust to order names that themselves contain "/" and digits: we just
// take the last two date tokens from the end of the string.
function uidDates(uid) {
  if (!uid) return "";
  const dates = uid.match(/\d{1,2}\/\d{1,2}\/\d{4}/g);
  if (dates && dates.length >= 2) return dates.slice(-2).join("  ·  ");
  if (dates && dates.length === 1) return dates[0];
  return uid; // unrecognised format — show as-is (hover still has the full value)
}

export function ImportReconcileGate({ user, onCleared }) {
  const toast = useToast();
  const isAdmin = user?.role === "ADMIN";
  const [rows, setRows] = useState(null);           // null = loading / not loaded, [] = none, [...] = conflicts
  const [busyId, setBusyId] = useState(null);
  // Per-row pending choice — nothing is applied until the admin clicks Update (a deliberate decision).
  // { [conflictId]: { action: "KEEP_EXISTING" | "KEEP_NEW" | "CHANGE", value?: string } }
  const [choice, setChoice] = useState({});
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    if (!isAdmin) { setRows([]); return; }
    try {
      const data = await api.valueConflicts();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      // never block the whole inventory because the gate failed to load — just skip it
      setRows([]);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const remove = (id) => {
    setRows((cur) => {
      const next = (cur || []).filter((r) => r.id !== id);
      if (next.length === 0 && typeof onCleared === "function") onCleared();
      return next;
    });
  };

  // Step 1 — pick (just highlights, applies nothing).
  const pick = (id, action, value) => setChoice((c) => ({ ...c, [id]: { action, value } }));

  // Step 2 — commit the highlighted choice for this row.
  const commit = async (row) => {
    const c = choice[row.id];
    if (!c) return;                                   // Update is disabled until something is picked
    const value = c.action === "CHANGE" ? c.value : null;
    if (c.action === "CHANGE" && !value) { toast("Pick a value to change to first", "err"); return; }
    setBusyId(row.id);
    try {
      await api.resolveValueConflict(row.id, c.action, value);
      const label =
        c.action === "KEEP_EXISTING" ? `Kept "${row.existingValue}"` :
        c.action === "KEEP_NEW" ? `Set to "${row.newValue}" — sent to To-Assign` :
        `Set to "${value}" — sent to To-Assign`;
      toast(label, "ok");
      setChoice((cur) => { const n = { ...cur }; delete n[row.id]; return n; });
      remove(row.id);
    } catch (e) {
      toast(e.message || "Couldn't update this record", "err");
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin) return null;
  if (rows === null) return null;            // still loading — don't flash an empty dialog
  if (rows.length === 0) return null;        // nothing to reconcile

  // --- pagination (keeps the dialog at a fixed, compact height) ---
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);   // self-heals when rows are cleared off the last page
  const start = safePage * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);
  const firstShown = start + 1;
  const lastShown = start + pageRows.length;

  // compact, single-line action controls
  const BTN = { height: 34, padding: "0 10px", fontSize: 12, whiteSpace: "nowrap" };
  const SEL = { height: 34, width: 138, minWidth: 138, fontSize: 12 };

  const footer = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 12 }}>
      <span style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>
        {total > PAGE_SIZE
          ? <>Showing <strong>{firstShown}–{lastShown}</strong> of <strong>{total}</strong></>
          : <><strong>{total}</strong> record{total === 1 ? "" : "s"} to clear</>}
      </span>
      {total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn btn-ghost btn-sm" disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}>‹ Prev</button>
          <span style={{ fontSize: 12.5, color: "#64748b", minWidth: 92, textAlign: "center" }}>
            Page {safePage + 1} of {totalPages}
          </span>
          <button className="btn btn-ghost btn-sm" disabled={safePage >= totalPages - 1}
            onClick={() => setPage(safePage + 1)}>Next ›</button>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      locked
      wide
      className="reconcile-gate"
      title={`Reconcile changed values (${total})`}
      sub={`${total} record${total === 1 ? "" : "s"} changed value since they were allocated — clear these before allocating.`}
      foot={footer}
    >
      {/* Scoped layout for this dialog only. The UID is compacted to its dates, so everything fits
          with no sideways scroll; overflow-x is a last-resort safety for pathological rows only. */}
      <style>{`
        .modal.reconcile-gate { max-width: min(1280px, 96vw); }
        .reconcile-gate .recon-wrap { overflow-x: auto; overflow-y: visible; }
        .reconcile-gate .recon-wrap::-webkit-scrollbar { height: 8px; }
        .reconcile-gate .recon-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        .reconcile-gate table.tbl { width: 100%; table-layout: auto; }
        .reconcile-gate table.tbl th, .reconcile-gate table.tbl td { white-space: nowrap; }
        .reconcile-gate table.tbl td.uid { font-family: monospace; font-size: 11.5px; color: #475569; }
      `}</style>

      <p style={{ margin: "0 0 12px", lineHeight: 1.55, color: "#475569", fontSize: 13 }}>
        Each of these is assigned to someone but hasn&apos;t been worked here, and today&apos;s sheet brought it
        in with a different value. Choose what the record&apos;s value should be. Keeping the new value (or
        choosing one) sends the record back to that stage&apos;s <strong>To&nbsp;assign</strong> for re-allocation.
      </p>

      <div className="recon-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ textAlign: "center" }}>#</th>
              <th>MRN</th>
              <th>UID (dates)</th>
              <th>Order</th>
              <th>Existing value</th>
              <th>New value</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => {
              const seq = start + i + 1;
              const busy = busyId === r.id;
              return (
                <tr key={r.id}>
                  <td style={{ textAlign: "center", fontWeight: 700, color: "#64748b" }}>{seq}</td>
                  <td style={{ fontFamily: "monospace" }}>{r.mrn}</td>
                  <td className="uid" title={r.uniqueId}>{uidDates(r.uniqueId)}</td>
                  <td>{r.orderName}</td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{r.existingValue}</span>
                    {r.assignedTo && <span style={{ fontSize: 11, color: "#64748b", marginLeft: 8 }}>· {r.assignedTo}</span>}
                  </td>
                  <td><span style={{ fontWeight: 600, color: "#1d4ed8" }}>{r.newValue}</span></td>
                  <td>
                    {(() => {
                      const sel = choice[r.id] || {};
                      const selStyle = (active) => active
                        ? { borderColor: "#4f46e5", background: "#eef2ff", color: "#3730a3", fontWeight: 700 }
                        : {};
                      const hasChoice = sel.action === "KEEP_EXISTING" || sel.action === "KEEP_NEW"
                        || (sel.action === "CHANGE" && sel.value);
                      return (
                    <div style={{ display: "flex", flexWrap: "nowrap", gap: 6, alignItems: "center" }}>
                      <button className="btn btn-ghost btn-sm" disabled={busy} style={{ ...BTN, ...selStyle(sel.action === "KEEP_EXISTING") }}
                        onClick={() => pick(r.id, "KEEP_EXISTING")}>
                        Keep existing
                      </button>
                      <button className="btn btn-ghost btn-sm" disabled={busy} style={{ ...BTN, ...selStyle(sel.action === "KEEP_NEW") }}
                        onClick={() => pick(r.id, "KEEP_NEW")}>
                        Keep new
                      </button>
                      <select
                        className="inp"
                        disabled={busy}
                        value={sel.action === "CHANGE" ? (sel.value || "") : ""}
                        onChange={(e) => pick(r.id, "CHANGE", e.target.value)}
                        style={{ ...SEL, ...selStyle(sel.action === "CHANGE" && !!sel.value) }}
                      >
                        <option value="">Change value…</option>
                        {VALUE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <button className="btn btn-primary btn-sm" disabled={busy || !hasChoice} style={BTN} onClick={() => commit(r)}>
                        Update
                      </button>
                      {busy && <Spinner />}
                    </div>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}